/**
 * Launcher V2 U7a — pure logic for the "Generate with AI" review surface.
 * Plan: plans/2026-09-10-001-launcher-v2-ai-copy-editing-and-usage-sidebar-plan.md
 * §U7 (review-mode + logic bullets), KTD 10. Spec B decisions 2, 3, 8, 12.
 *
 * DOM-free, no React/JSX imports (root CLAUDE.md layer-boundary rule) — same
 * reasoning as `copy-token-flags.ts`/`sequence-readiness.ts`: `bun:test` has
 * no DOM under `mini-apps/`, so any logic living inside a Glaze-importing
 * `.tsx` file is untestable there. `SequenceStepEditor.tsx`/`StepReviewCard.tsx`
 * import this module for the pieces below; neither re-derives them.
 */

import type { PlaybooksGenerationInputs, SegmentationConditions } from './schemas';

/**
 * The "Generate with AI" configure form's member-status field defaults to
 * whatever `cm_status` values the segmentation's OWN conditions already
 * constrain to — a rep who segmented on "Registered OR Attended" almost
 * certainly wants a message that only makes sense for those statuses, not a
 * blank multi-select they have to re-derive by eye.
 *
 * Walks every group's conditions (there is only one level of `groups` in
 * `SegmentationConditions` — no nested groups-within-groups in this schema)
 * looking for `field === 'cm_status'`. Only two operators contribute a
 * status: `'equals'` (a single string value) and `'in_any_of'` (an array of
 * strings) — every other operator on `cm_status` (`'not_equals'`, etc.) is
 * ignored, since it constrains AWAY from a status rather than selecting one.
 * Results are deduped into their first-encounter order (a rep re-stating the
 * same status in two groups should not see it twice), matching the order a
 * rep built their conditions in rather than an arbitrary sort.
 *
 * `null` (no segmentation conditions at all) returns `[]`.
 *
 * QA round 7, U8 (item 14) — `not_in_any_of` ("is not any of") is
 * deliberately NOT read here, same reasoning as `not_equals`: it constrains
 * AWAY from a set of statuses, not toward one, so it contributes nothing to
 * a status default.
 */
export function defaultStatusesFromConditions(conditions: SegmentationConditions | null): string[] {
  if (conditions === null) return [];
  const seen = new Set<string>();
  const result: string[] = [];
  const add = (value: string) => {
    if (seen.has(value)) return;
    seen.add(value);
    result.push(value);
  };

  for (const group of conditions.groups) {
    for (const cond of group.conditions) {
      if (cond.field !== 'cm_status') continue;
      if (cond.operator === 'equals' && typeof cond.value === 'string') {
        add(cond.value);
      } else if (cond.operator === 'in_any_of' && Array.isArray(cond.value)) {
        for (const value of cond.value) add(value);
      }
    }
  }
  return result;
}

/** The subset of `GenerateWithAIForm` (`generate-with-ai-store.ts`) {@link canGenerate} needs. */
export interface GenerateGateForm {
  campaignSfdcId: string | null;
  eventName: string;
}

/**
 * The drawer's Generate button gate: a campaign must be chosen
 * (`campaignSfdcId` set) AND the event name must be non-blank once
 * whitespace is trimmed — a rep who tabs through the form leaving only
 * spaces in the name field has not actually named anything.
 */
export function canGenerate(form: GenerateGateForm): boolean {
  return Boolean(form.campaignSfdcId) && form.eventName.trim().length > 0;
}

/**
 * terra-qyt round-2 fix U18 (W1) — the rep-facing hint shown under a
 * disabled "Generate with AI" trigger when the playbook has no seed
 * campaign yet. Playbook mode mounts its steps with `campaignSfdcId: ''`
 * (`PlayFlowShell.tsx`) before a seed is picked on Qualify — Generate must
 * stay disabled through that window, both because there is no campaign
 * context to generate FROM and because the server's `generation-context`
 * endpoint deterministically 400s on a blank `campaignSfdcId` query param
 * (`api.ts`'s `requiredQueryParam`).
 */
export const GENERATE_NEEDS_SEED_HINT = 'Pick a seed campaign on Qualify to generate copy.';

/** The subset of `SequencesStep`'s render state {@link generateTriggerState} needs. */
export interface GenerateTriggerInput {
  /** `''` in playbook mode before a seed is picked; a real sfdcId otherwise (play mode always has one — see `PlayFlowShell.tsx`). */
  campaignSfdcId: string;
  isGenerating: boolean;
  /** Whether the sequence's rendered head has loaded — mirrors the trigger's pre-existing `renderedVersion !== null` gate. */
  hasHead: boolean;
}

/**
 * terra-qyt round-2 fix U18 (W1) — the "Generate with AI" trigger's full
 * disabled/hint state, in one place so `SequencesStep.tsx`'s JSX stays a
 * plain read of this result rather than re-deriving the seed gate inline.
 * `disabled` is a strict superset of the button's PRE-EXISTING gates
 * (`isGenerating`, no rendered head) plus the new blank-seed gate; `hint`
 * is non-null ONLY for the blank-seed case — the other two disabled
 * reasons already have their own affordances (a spinner/label swap, a
 * loading sequence) and do not need this hint.
 */
export function generateTriggerState(input: GenerateTriggerInput): { disabled: boolean; hint: string | null } {
  const disabled = input.isGenerating || !input.hasHead || input.campaignSfdcId === '';
  const hint = input.campaignSfdcId === '' ? GENERATE_NEEDS_SEED_HINT : null;
  return { disabled, hint };
}

/**
 * terra-qyt round-2 fix U18 (W1) — `GenerateWithAIDrawer#handleApply`'s
 * campaign-mismatch refusal copy, moved here in round-3 fix U24 (Design
 * decision 8) so the drawer, the two `SequencesStep` usage-sidebar decision
 * handlers, and this module's own bun test all share ONE string rather than
 * `SequencesStep.tsx` importing a private drawer constant or re-declaring
 * its own copy. Distinct from `HEAD_MOVED_DETAIL` (still local to the
 * drawer) because the underlying problem is different — the sequence
 * content didn't change, the CAMPAIGN the copy was written for did.
 */
export const CAMPAIGN_MOVED_DETAIL = 'This copy was generated for a different campaign — discard and regenerate for the current one.';

/**
 * terra-qyt round-3 fix U24 (W1 residual, Design decision 8) — the
 * belt-and-suspenders check `handleUsageSidebarUpdateAll`/
 * `handleUsageSidebarSaveNew` (`SequencesStep.tsx`) run BEFORE writing a
 * pending usage-sidebar save whose copy came from AI Apply
 * (`pending.generationInputs`): if the campaign that copy was generated FOR
 * no longer matches the host's CURRENT campaign, the write must be refused.
 *
 * `generationInputs` is `undefined`/`null` for a hand-typed save or a
 * delete — those are campaign-neutral (no generation provenance to
 * mismatch), so this always returns `false` for them; `hostCampaignSfdcId`
 * is the step's own `campaignSfdcId` prop, `''` when a playbook has no seed
 * yet, which can never equal a real generated campaign id.
 */
export function generationCampaignMismatch(
  generationInputs: PlaybooksGenerationInputs | null | undefined,
  hostCampaignSfdcId: string,
): boolean {
  return !!generationInputs && generationInputs.campaignSfdcId !== hostCampaignSfdcId;
}

/** The subset of `reviewTally`'s (`generated-copy-store.ts`) return shape {@link tallyText} needs. */
export interface ReviewTallyCounts {
  reviewed: number;
  total: number;
}

/** The review footer's progress copy — "N of M reviewed" (M may be 0 before any step has staged). */
export function tallyText(tally: ReviewTallyCounts): string {
  return `${tally.reviewed} of ${tally.total} reviewed`;
}

/**
 * The streaming-progress copy shown while a generation's `GeneratedCopyPending`
 * frames are still arriving — "Writing step N of M…". `pending` is the count
 * of steps that have ALREADY staged (`Object.keys(pendingEntry.steps).length`);
 * the step the model is actively writing right now is one past that count,
 * so `pending = 1` (one step landed) reports "Writing step 2 of 8…", and
 * `pending = 0` (nothing landed yet) reports "Writing step 1 of 8…".
 */
export function progressText(pending: number, total: number): string {
  return `Writing step ${pending + 1} of ${total}…`;
}

/** The subset of `StepReview` (`generated-copy-store.ts`) {@link reviewBadgeState} needs. */
export interface ReviewBadgeInput {
  decision: 'pending' | 'accepted' | 'rejected';
  unchanged: boolean;
}

export type ReviewBadgeStatus = 'neutral' | 'action' | 'success';

export interface ReviewBadgeState {
  label: string;
  status: ReviewBadgeStatus;
}

/**
 * The header `StatusBadge` a review card shows, from the step's decision +
 * `unchanged` flag. `unchanged` wins over `decision` — a step the model
 * reproduced verbatim needs no rep decision (`reviewTally`'s own contract:
 * it already counts as reviewed) and is never shown as "Staged" just because
 * `decision` still reads `'pending'`.
 */
export function reviewBadgeState(review: ReviewBadgeInput): ReviewBadgeState {
  if (review.unchanged) return { label: 'No change', status: 'neutral' };
  if (review.decision === 'accepted') return { label: 'Accepted', status: 'success' };
  if (review.decision === 'rejected') return { label: 'Rejected', status: 'neutral' };
  return { label: 'Staged', status: 'action' };
}

/**
 * The fields of a fresh configure-form seed (D6/A9) — the same field set as
 * `GenerateWithAIForm` (`generate-with-ai-store.ts`) minus `campaignSfdcId`,
 * which the caller already knows before it ever asks for a seed (a campaign
 * must be chosen before `generation-context` — the source of `context`
 * below — can even be fetched).
 */
export interface GenerateFormSeed {
  eventName: string;
  campaignDescription: string;
  memberStatuses: string[];
  instruction: string;
  sourceMaterial: string;
}

/**
 * The subset of `PlaybooksGenerationPreset['lastInputs']` (`schemas.ts`)
 * {@link seedFormFrom} needs — already exactly {@link GenerateFormSeed}'s
 * shape, since the preset schema OMITS the same run-specific fields
 * (`generatedAt`/`model`/`strategy`/`sourceHeadVersionId`) from
 * `PlaybooksGenerationInputsSchema` that this form never carries either.
 */
export type GenerateLastInputsSeed = GenerateFormSeed;

/** The subset of `PlaybooksGenerationContextResponse` (`schemas.ts`) {@link seedFormFrom} needs for its fallback defaults. */
export interface GenerateContextSeed {
  eventNameSeed: string;
  campaign: { description: string | null };
  defaultInstruction: string;
}

/**
 * A fresh (no staged generation yet) session's configure-form seed (D6/A9).
 * Preselects the rep's OWN prior generation inputs on this sequence
 * (`lastInputs` — `playbooks_sequence.generation_preset.lastInputs`, `null`
 * until a generation has ever happened) over the campaign's own defaults
 * (`context`, from `GET .../generation-context`) — a rep re-running
 * Generate on the same campaign almost certainly wants the same
 * instruction/statuses/source material they used last time, not the
 * campaign's raw defaults again.
 *
 * Caller's responsibility, NOT this function's: confirming `lastInputs`
 * belongs to the CAMPAIGN currently selected (the preset is keyed on the
 * sequence, not the campaign — a rep can pick a different campaign for the
 * same sequence on a later run) before passing it in; pass `null` instead
 * when it doesn't match.
 *
 * This is the ONLY seed order for a fresh session. It has nothing to do
 * with `generated-copy-store.ts`'s `generationSnapshot` / this store's
 * `openGenerateSession({ restoreFrom })` (D6's OTHER seed path) — that one
 * restores from an ALREADY-STAGED generation's own recorded inputs and wins
 * over both `lastInputs` and `context` whenever it applies.
 */
export function seedFormFrom(input: {
  lastInputs: GenerateLastInputsSeed | null;
  context: GenerateContextSeed;
}): GenerateFormSeed {
  if (input.lastInputs !== null) {
    return { ...input.lastInputs };
  }
  return {
    eventName: input.context.eventNameSeed,
    campaignDescription: input.context.campaign.description ?? '',
    memberStatuses: [],
    instruction: input.context.defaultInstruction,
    sourceMaterial: '',
  };
}
